import { useState } from 'react';
import { Palette, Loader2, Check } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useCampaignStore } from '@/stores/campaignStore';
import { useUpdateTenantCampaign } from '@/hooks/queries/useTenantCampaigns';
import type { TenantCampaignWithCapabilitiesDto, UpdateCampaignRequest } from '@signalvote/shared';
import { cn } from '@/lib/utils';

// ADR-022 — branding no longer carries state / jurisdiction / office level.
// The campaign's single jurisdiction (rulesetCode) and office level live on
// finance settings; the redundant branding encodings were removed.
interface BrandingFormState {
  candidateName: string;
  campaignTitle: string;
  office: string;
  paidForBy: string;
  electionDate: string;
  campaignEmail: string;
  campaignPhone: string;
  campaignWebsite: string;
  campaignAddress: string;
  facebookUrl: string;
  twitterUrl: string;
  instagramUrl: string;
  campaignCreatorType: string;
  teamSize: string;
}

const BRANDING_KEYS: (keyof BrandingFormState)[] = [
  'candidateName', 'campaignTitle', 'office', 'paidForBy',
  'electionDate', 'campaignEmail', 'campaignPhone', 'campaignWebsite',
  'campaignAddress', 'facebookUrl', 'twitterUrl', 'instagramUrl',
  'campaignCreatorType', 'teamSize',
];

const CREATOR_TYPE_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'Candidate', label: 'Candidate' },
  { value: 'CampaignStaff', label: 'Campaign Staff' },
];

const TEAM_SIZE_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'Solo', label: 'Just me' },
  { value: 'Small', label: '2-5' },
  { value: 'Medium', label: '6-20' },
  { value: 'Large', label: '20+' },
];

function buildFormState(campaign: Record<string, unknown> | null | undefined): BrandingFormState {
  const state = {} as BrandingFormState;
  for (const key of BRANDING_KEYS) {
    state[key] = ((campaign?.[key] as string) || '');
  }
  return state;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toRecord = (obj: unknown): Record<string, unknown> | null => obj as any;

interface CampaignBrandingCardProps {
  /**
   * Hide the free-text Election Date input. Set by the co-located Campaign &
   * Committee section when a structured general election date already exists on
   * finance settings, so the election date is shown once. The field is retained
   * (and still saved) whenever it is visible.
   */
  hideElectionDate?: boolean;
}

export function CampaignBrandingCard({ hideElectionDate = false }: CampaignBrandingCardProps = {}) {
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  const canManageTeam = useCampaignStore((s) => s.canManage('TeamManagement'));

  if (!selectedCampaign || !canManageTeam) return null;

  // Keyed on the campaign: switching campaigns starts a fresh form, while a
  // background refetch of the SAME campaign leaves an in-progress edit alone.
  return (
    <BrandingForm
      key={selectedCampaign.id}
      selectedCampaign={selectedCampaign}
      hideElectionDate={hideElectionDate}
    />
  );
}

function BrandingForm({
  selectedCampaign,
  hideElectionDate,
}: {
  selectedCampaign: TenantCampaignWithCapabilitiesDto;
  hideElectionDate: boolean;
}) {
  const [form, setForm] = useState<BrandingFormState>(() => buildFormState(toRecord(selectedCampaign)));
  const [saveSuccess, setSaveSuccess] = useState(false);

  const updateCampaign = useUpdateTenantCampaign();

  const hasChanges = BRANDING_KEYS.some(
    (key) => form[key] !== ((selectedCampaign[key as keyof typeof selectedCampaign] as string) || '')
  );

  const handleSave = () => {
    setSaveSuccess(false);
    const data: Partial<UpdateCampaignRequest> = {
      candidateName: form.candidateName,
      campaignTitle: form.campaignTitle,
      office: form.office,
      paidForBy: form.paidForBy,
      electionDate: form.electionDate,
      campaignEmail: form.campaignEmail,
      campaignPhone: form.campaignPhone,
      campaignWebsite: form.campaignWebsite,
      campaignAddress: form.campaignAddress,
      facebookUrl: form.facebookUrl,
      twitterUrl: form.twitterUrl,
      instagramUrl: form.instagramUrl,
      campaignCreatorType: form.campaignCreatorType ? form.campaignCreatorType as UpdateCampaignRequest['campaignCreatorType'] : null,
      teamSize: form.teamSize ? form.teamSize as UpdateCampaignRequest['teamSize'] : null,
    };
    updateCampaign.mutate(
      { id: selectedCampaign.id, data },
      {
        onSuccess: () => {
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 3000);
        },
      }
    );
  };

  const renderField = (
    key: keyof BrandingFormState,
    label: string,
    opts?: { type?: string; placeholder?: string }
  ) => (
    <div>
      <label htmlFor={`branding-${key}`} className="text-sm font-medium text-muted-foreground">
        {label}
      </label>
      <input
        id={`branding-${key}`}
        type={opts?.type || 'text'}
        value={form[key]}
        onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
        placeholder={opts?.placeholder}
        className={cn(
          'w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        )}
      />
    </div>
  );

  const renderSelect = (
    key: keyof BrandingFormState,
    label: string,
    options: readonly { value: string; label: string }[]
  ) => (
    <div>
      <label htmlFor={`branding-${key}`} className="text-sm font-medium text-muted-foreground">
        {label}
      </label>
      <select
        id={`branding-${key}`}
        value={form[key]}
        onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
        className={cn(
          'w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        )}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Palette className="h-5 w-5" />
          Campaign Branding
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h3 className="text-sm font-semibold mb-3">Campaign Setup</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderSelect('campaignCreatorType', 'Your Role', CREATOR_TYPE_OPTIONS)}
            {renderSelect('teamSize', 'Team Size', TEAM_SIZE_OPTIONS)}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-3">Campaign Identity</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('candidateName', 'Candidate Name', { placeholder: 'e.g. John Smith' })}
            {renderField('campaignTitle', 'Campaign Title', { placeholder: 'e.g. Smith for Mayor' })}
            {renderField('office', 'Office', { placeholder: 'e.g. Mayor' })}
            {renderField('paidForBy', 'Paid For By', { placeholder: 'e.g. Friends of John Smith' })}
            {!hideElectionDate && renderField('electionDate', 'Election Date', { type: 'date' })}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-3">Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('campaignEmail', 'Email', { type: 'email', placeholder: 'campaign@example.com' })}
            {renderField('campaignPhone', 'Phone', { type: 'tel', placeholder: '(555) 123-4567' })}
            {renderField('campaignWebsite', 'Website', { type: 'url', placeholder: 'https://www.example.com' })}
            {renderField('campaignAddress', 'Address', { placeholder: '123 Main St, Springfield, IL 62701' })}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-3">Social Media</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('facebookUrl', 'Facebook URL', { type: 'url', placeholder: 'https://facebook.com/...' })}
            {renderField('twitterUrl', 'Twitter / X URL', { type: 'url', placeholder: 'https://x.com/...' })}
            {renderField('instagramUrl', 'Instagram URL', { type: 'url', placeholder: 'https://instagram.com/...' })}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || updateCampaign.isPending}
          >
            {updateCampaign.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                Saving...
              </>
            ) : (
              'Save Branding'
            )}
          </Button>
          {saveSuccess && (
            <span className="flex items-center gap-1 text-sm text-green-600">
              <Check className="h-4 w-4" />
              Saved
            </span>
          )}
          {updateCampaign.isError && (
            <span className="text-sm text-destructive">
              Failed to save. Please try again.
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
